import React, { useState, useEffect, useRef } from "react";
import "./ExamSystem.css";
import questionsData from "../Utilities/questionData.json";
import InstructionsModal from "../Components/InstructionsModal/InstructionsModal";
import ShowResult from "../Components/ShowResult/ShowResult";
import ReviewScreen from "../Components/ReviewScreen/ReviewScreen";

const ExamSystem = () => {
  // Existing states
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSubjectForm, setShowSubjectForm] = useState(true);
  const [classLevel, setClassLevel] = useState("");
  const [educationLevel, setEducationLevel] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");

  // New states for enhanced features
  const [showInstructions, setShowInstructions] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [wrongQuestions, setWrongQuestions] = useState([]);
  const [sectionsMap, setSectionsMap] = useState({});
  const [sectionsOrder, setSectionsOrder] = useState([]);
  const [sectionsMeta, setSectionsMeta] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [showSectionComplete, setShowSectionComplete] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true); // Auto-advance after answer selection
  
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);

  const educationOptions = [
    { label: "Primary", value: "primary", icon: "school-outline" },
    { label: "O-Level", value: "secondary", icon: "library-outline" },
    { label: "A-Level", value: "advanceLevel", icon: "school" },
  ];

  const classLevels = {
    primary: ["Standard 1", "Standard 2", "Standard 3", "Standard 4", "Standard 5", "Standard 6", "Standard 7"],
    secondary: ["Form I", "Form II", "Form III", "Form IV"],
    advanceLevel: ["Form V", "Form VI"],
  };

  // Extract available subjects from JSON data
  useEffect(() => {
    const availableSubjects = questionsData.subjects.map(subject => ({
      id: subject.id,
      name: subject.name
    }));
    setSubjects(availableSubjects);
  }, []);

  const handleGenerateQuestions = () => {
    if (!educationLevel || !classLevel || !selectedSubject) {
      alert("Please complete all fields.");
      return;
    }

    setLoading(true);

    // Find the subject in the JSON data
    const subjectData = questionsData.subjects.find(
      s => s.name.toLowerCase() === selectedSubject.toLowerCase()
    );

    if (!subjectData) {
      alert(`No questions found for subject: ${selectedSubject}`);
      setLoading(false);
      return;
    }

    // Find the class level data
    const classData = subjectData.classLevels[classLevel];
    if (!classData) {
      alert(`No questions found for class level: ${classLevel}`);
      setLoading(false);
      return;
    }

    try {
      // Extract and organize questions into sections - take 15 questions from each section
      let tempSections = {};
      let order = [];

      const shuffleArray = (arr) => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };

      if (classData.examSets && typeof classData.examSets === "object") {
        Object.entries(classData.examSets).forEach(([setName, qArr]) => {
          // Take 15 questions from each section
          tempSections[setName] = {
            questions: shuffleArray(qArr).slice(0, 15),
            completed: false,
            score: 0
          };
          order.push(setName);
        });
      } else if (Array.isArray(classData)) {
        // If no sections, create one main section with 45 questions
        const name = "Main Section";
        tempSections[name] = {
          questions: shuffleArray(classData).slice(0, 45),
          completed: false,
          score: 0
        };
        order.push(name);
      }

      // Build flattenedQuestions and sectionsMeta
      const flat = [];
      const meta = [];
      let runningIndex = 0;
      order.forEach(sectionName => {
        const qArr = tempSections[sectionName].questions || [];
        meta.push({
          name: sectionName,
          startIndex: runningIndex,
          length: qArr.length
        });
        qArr.forEach(q => flat.push(q));
        runningIndex += qArr.length;
      });

      setSectionsMap(tempSections);
      setSectionsOrder(order);
      setExamQuestions(flat);
      setSectionsMeta(meta);
      setCurrentSectionIndex(0);
      setCurrentQuestion(0);
      setSelectedOptions({});
      
      // Show instructions instead of going directly to exam
      setShowInstructions(true);
      setShowSubjectForm(false);
      
    } catch (error) {
      console.error("Error loading questions:", error);
      alert("Error loading questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartExam = () => {
    setShowInstructions(false);
    setExamStarted(true);
    startTimeRef.current = Date.now();
    
    // Start timer
    timerRef.current = setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
  };

  // Timer cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Check if current question is the last question of current section
  const isLastQuestionOfSection = () => {
    const currentSectionMeta = sectionsMeta[currentSectionIndex];
    if (!currentSectionMeta) return false;
    
    const lastQuestionOfSection = currentSectionMeta.startIndex + currentSectionMeta.length - 1;
    return currentQuestion === lastQuestionOfSection;
  };

  // Option selection with auto-advance
  const handleOptionSelect = (optionIndex) => {
    const newSelectedOptions = {
      ...selectedOptions,
      [currentQuestion]: optionIndex,
    };
    setSelectedOptions(newSelectedOptions);

    // Auto-advance to next question after selection
    if (autoAdvance) {
      setTimeout(() => {
        // Check if this is the last question of the current section
        if (isLastQuestionOfSection()) {
          handleSectionComplete();
        } else if (currentQuestion < examQuestions.length - 1) {
          setCurrentQuestion(prev => prev + 1);
        } else {
          handleSectionComplete();
        }
      }, 300); // Small delay for better UX
    }
  };

  const handleNext = () => {
    // Check if this is the last question of the current section
    if (isLastQuestionOfSection()) {
      handleSectionComplete();
    } else if (currentQuestion < examQuestions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      handleSectionComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    // Check if this is the last question of the current section
    if (isLastQuestionOfSection()) {
      handleSectionComplete();
    } else if (currentQuestion < examQuestions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      handleSectionComplete();
    }
  };

  const calculateSectionScoreByIndex = (sectionIndex) => {
    const meta = sectionsMeta[sectionIndex];
    if (!meta) return 0;
    let sectionScore = 0;
    for (let i = 0; i < meta.length; i++) {
      const globalIndex = meta.startIndex + i;
      const q = examQuestions[globalIndex];
      if (selectedOptions[globalIndex] !== undefined && selectedOptions[globalIndex] === q.correctAnswer) {
        sectionScore++;
      }
    }
    return sectionScore;
  };

  const handleSectionComplete = () => {
    const sectionScore = calculateSectionScoreByIndex(currentSectionIndex);
    const sectionName = sectionsOrder[currentSectionIndex];
    setSectionsMap(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        completed: true,
        score: sectionScore
      }
    }));

    setShowSectionComplete(true);
  };

  const proceedToNextSection = () => {
    const nextIndex = currentSectionIndex + 1;
    setShowSectionComplete(false);

    if (nextIndex < sectionsOrder.length) {
      setCurrentSectionIndex(nextIndex);
      setCurrentQuestion(sectionsMeta[nextIndex].startIndex);
    } else {
      handleFinalSubmit();
    }
  };

  const handleFinalSubmit = () => {
    let correctAnswers = 0;
    const wrongOnes = [];

    examQuestions.forEach((q, i) => {
      if (selectedOptions[i] === q.correctAnswer) {
        correctAnswers++;
      } else {
        wrongOnes.push({
          ...q,
          globalIndex: i,
          selected: selectedOptions[i],
          userAnswer: selectedOptions[i] !== undefined ? q.options[selectedOptions[i]] : "Not attempted"
        });
      }
    });

    setScore(correctAnswers);
    setWrongQuestions(wrongOnes);
    setShowResults(true); 
    setExamStarted(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const handleReview = () => {
    setShowResults(false);
    setShowReview(true);
  };

  const handleRetry = () => {
    setCurrentQuestion(0);
    setSelectedOptions({});
    setShowResults(false);
    setShowReview(false);
    setShowSubjectForm(true);
    setScore(0);
    setTimeSpent(0);
    setSelectedSubject("");
    setClassLevel("");
    setEducationLevel("");
    setExamStarted(false);
  };

  const handleBackToResults = () => {
    setShowReview(false);
    setShowResults(true);
  };

  const handleBackToHome = () => {
    setShowResults(false);
    setShowReview(false);
    setShowSubjectForm(true);
    setSelectedSubject("");
    setClassLevel("");
    setEducationLevel("");
    setScore(0);
    setTimeSpent(0);
  };

  const formatTime = (s) => {
    const h = Math.floor(s / 3600).toString().padStart(2, "0");
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
    const sec = s % 60;
    return `${h}:${m}:${sec.toString().padStart(2, "0")}`;
  };

  // Instructions based on subject
  const englishInstructions = [
    "General Instructions",
    "TIME", 
    "Begin Exam",
    "This exam consists of multiple choice questions.",  
    "Answer all questions carefully.",
    "Time is limited. Focus and avoid distractions.",
    "Each question has only one correct answer.",
    "Do not close the app during the exam.",
    "Click 'Begin Exam' to get started."
  ];

  const kiswahiliInstructions = [
    "Maelekezo kwa ujumla",
    "MUDA",
    "Anza Mtihani",
    "Mtihani huu unajumuisha maswali ya kuchagua",
    "Jibu maswali yote kwa uangalifu.",
    "Muda ni mdogo. Fanya kwa utulivu",
    "Kila swali lina jibu moja tu sahihi.",
    "Usifunge programu wakati wa mtihani.",
    "Bofya 'Anza Mtihani' kuanza."
  ];

  // Render different screens based on state
  if (showReview) {
    return (
      <ReviewScreen
        wrongQuestions={wrongQuestions}
        subject={selectedSubject}
        classLevel={classLevel}
        onBack={handleBackToResults}
      />
    );
  }

  if (showResults) {
    return (
      <ShowResult
        score={score}
        totalQuestions={examQuestions.length}
        questions={examQuestions}
        selectedOptions={selectedOptions}
        timeUsed={timeSpent}
        onReview={handleReview}
        onRetry={handleRetry}
        onHome={handleBackToHome}
        sectionsMeta={sectionsMeta}
        sectionsOrder={sectionsOrder}
      />
    );
  }

  if (showInstructions) {
    return (
      <InstructionsModal
        visible={showInstructions}
        onClose={() => {
          setShowInstructions(false);
          setShowSubjectForm(true);
        }}
        onStart={handleStartExam}
        examDuration={examQuestions.length}
        subject={selectedSubject}
        instructions={
          selectedSubject.toLowerCase() === "kiswahili"
            ? kiswahiliInstructions
            : englishInstructions
        }
      />
    );
  }

  // Subject and Class Level Form
  if (showSubjectForm) {
    return (
      <div className="exam-system">
        <div className="exam-container">
          <div className="subject-form">
            <h2>🎓 Create Your Exam</h2>
            <p>Enter your class level and subject to generate questions</p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleGenerateQuestions();
            }}>
              <div className="form-group">
                <label htmlFor="educationLevel">Education Level:</label>
                <select
                  id="educationLevel"
                  value={educationLevel}
                  onChange={(e) => {
                    setEducationLevel(e.target.value);
                    setClassLevel("");
                  }}
                  required
                >
                  <option value="">Select your education level</option>
                  {educationOptions.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>
                
              {educationLevel && (
                <div className="form-group">
                  <label htmlFor="classLevel">Class Level:</label>
                  <select
                    id="classLevel"
                    value={classLevel}
                    onChange={(e) => setClassLevel(e.target.value)}
                    required
                  >
                    <option value="">Select your class level</option>
                    {classLevels[educationLevel].map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="subject">Subject:</label>
                <select
                  id="subject"
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  required
                >
                  <option value="">Select your subject</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <button 
                type="submit" 
                className="generate-btn"
                disabled={loading}
              >
                {loading ? "Generating Questions..." : "Generate Exam Questions"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="exam-system">
        <div className="exam-container">
          <h2>🧠 Generating your {classLevel} {selectedSubject} exam questions...</h2>
        </div>
      </div>
    );
  }

  // Section Complete Modal
  if (showSectionComplete) {
    const currentSectionName = sectionsOrder[currentSectionIndex];
    const currentSectionScore = calculateSectionScoreByIndex(currentSectionIndex);
    const currentSectionLength = sectionsMeta[currentSectionIndex]?.length || 0;
    
    return (
      <div className="section-complete-modal">
        <div className="section-complete-content">
          <h2>{currentSectionName ? `${currentSectionName} Complete!` : `Section Complete!`}</h2>
          <div className="section-score">
            Score: {currentSectionScore} / {currentSectionLength}
          </div>
          <p>
            {currentSectionIndex < sectionsOrder.length - 1
              ? "Ready to proceed to the next section?"
              : "All sections completed! Ready to see your results?"}
          </p>
          <button className="proceed-button" onClick={proceedToNextSection}>
            {currentSectionIndex < sectionsOrder.length - 1 ? "Proceed to Next Section" : "View Results"}
          </button>
        </div>
      </div>
    );
  }

  // Exam Interface
  if (examStarted && examQuestions.length > 0) {
    const currentQ = examQuestions[currentQuestion];
    const progress = ((currentQuestion + 1) / examQuestions.length) * 100;
    
    // Safer way to get current section meta
    const currentSectionMeta = sectionsMeta[currentSectionIndex] || sectionsMeta.find(meta => 
      currentQuestion >= meta.startIndex && currentQuestion < meta.startIndex + meta.length
    );

    const isLastQuestionOfCurrentSection = isLastQuestionOfSection();
    const isLastQuestionOfExam = currentQuestion === examQuestions.length - 1;
    const isQuestionAnswered = selectedOptions[currentQuestion] !== undefined;

    // Calculate current question in section safely
    const currentQuestionInSection = currentSectionMeta 
      ? currentQuestion - currentSectionMeta.startIndex + 1
      : 1;
    const totalQuestionsInSection = currentSectionMeta?.length || 15;

    return (
      <div className="exam-system">
        <div className="exam-container">
          <div className="exam-header">
            <h3>{classLevel} - {selectedSubject}</h3>
            <div className="timer">Time: {formatTime(timeSpent)}</div>
          </div>

          {/* Section Progress */}
          {sectionsOrder.length > 1 && (
            <div className="section-indicator">
              <div className="current-section">
                {currentSectionMeta?.name || "Section"} 
                {currentSectionMeta && (
                  <span className="section-question-counter">
                    ({currentQuestionInSection} of {totalQuestionsInSection})
                  </span>
                )}
              </div>
              <div className="section-progress">
                {sectionsOrder.map((section, index) => (
                  <div key={section} className="section-dot-container">
                    <div
                      className={`section-dot ${
                        sectionsMap[section]?.completed ? 'completed' : ''
                      } ${currentSectionIndex === index ? 'active' : ''}`}
                    />
                    {index < sectionsOrder.length - 1 && <div className="section-line" />}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="exam-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="progress-text">
              <span>
                Question {currentQuestion + 1} of {examQuestions.length}
              </span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
          </div>

          <div className="exam-content">
            <h2>{currentQ.question}</h2>
            <div className="options-grid">
              {currentQ.options.map((opt, i) => (
                <div
                  key={i}
                  className={`option-card ${
                    selectedOptions[currentQuestion] === i ? "selected" : ""
                  }`}
                  onClick={() => handleOptionSelect(i)}
                >
                  <strong>{String.fromCharCode(65 + i)}.</strong> {opt}
                </div>
              ))}
            </div>

            <div className="navigation-buttons">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className="nav-btn btn-prev"
              >
                ← Previous
              </button>

              {isLastQuestionOfExam ? (
                <button
                  onClick={handleFinalSubmit}
                  className="nav-btn btn-submit"
                >
                  Submit Exam ✓
                </button>
              ) : (
                <button
                  onClick={isQuestionAnswered ? handleNext : handleSkip}
                  className={`nav-btn ${isQuestionAnswered ? 'btn-next' : 'btn-skip'} ${
                    isLastQuestionOfCurrentSection ? 'btn-section-complete' : ''
                  }`}
                >
                  {isLastQuestionOfCurrentSection 
                    ? (isQuestionAnswered ? 'Complete Section →' : 'Complete Section →')
                    : (isQuestionAnswered ? 'Next →' : 'Skip →')
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="exam-system">
      <div className="exam-container">
        <span>⚠️ No questions generated. Please try again.</span>
        <button onClick={() => setShowSubjectForm(true)} className="restart-btn">
          Back to Subject Selection
        </button>
      </div>
    </div>
  );
};

export default ExamSystem;